import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  fetchPackaging,
  submitVerification,
  searchBins,
  recordEvent,
} from '../lib/api.js';
import { useConsumerSession } from '../hooks/useConsumerSession.js';
import { useConsumerAuth } from '../context/ConsumerAuthContext.jsx';

const outcomeStyles = {
  PACT: 'bg-emerald-50 border-emerald-200 text-emerald-800',
  PACT_AND_OTHER: 'bg-amber-50 border-amber-200 text-amber-800',
  CURBSIDE: 'bg-sky-50 border-sky-200 text-sky-700',
  TRASH: 'bg-rose-50 border-rose-200 text-rose-700',
  NEEDS_CLEANING: 'bg-yellow-50 border-yellow-200 text-yellow-700',
  UNKNOWN: 'bg-neutral-50 border-neutral-200 text-neutral-700',
};

function ResultPanel({ verification }) {
  if (!verification) return null;

  const { outcome, guidance } = verification;
  const className = outcomeStyles[outcome] || outcomeStyles.UNKNOWN;

  return (
    <div className={`card border-2 ${className}`}>
      <div className="space-y-3">
        <div className="space-y-2">
          <span className="badge">{outcome.replace(/_/g, ' ')}</span>
          <h3 className="text-xl font-semibold text-neutral-900">{guidance?.title}</h3>
          <p className="text-sm text-neutral-700">{guidance?.description}</p>
        </div>
        <RecommendedActions verification={verification} />
      </div>
    </div>
  );
}

function RecommendedActions({ verification }) {
  const outcome = verification.outcome;
  if (outcome === 'PACT' || outcome === 'PACT_AND_OTHER') {
    return (
      <div className="flex flex-wrap gap-3 text-sm">
        <Link to="/bin-finder" className="btn btn-outline rounded-full">
          Find a Pact bin
        </Link>
        <Link to="/mail-back" className="btn btn-outline rounded-full">
          Request mail-back label
        </Link>
        <Link to="/learn" className="btn btn-outline rounded-full">
          Packing guidelines
        </Link>
      </div>
    );
  }
  if (outcome === 'CURBSIDE') {
    return (
      <div className="space-y-2 text-sm text-neutral-700">
        <p>
          This packaging is best handled by your local curbside program. Rinse it, check your municipality&apos;s rules, and recycle it with your household pickup.
        </p>
        <Link to="/learn" className="btn btn-outline rounded-full inline-flex w-fit">
          Curbside tips
        </Link>
      </div>
    );
  }
  if (outcome === 'TRASH') {
    return (
      <div className="space-y-2 text-sm text-neutral-700">
        <p>
          We can&apos;t recycle this item yet. Place it in the trash to avoid contaminating Pact streams and check back as we expand accepted materials.
        </p>
        <Link to="/learn" className="btn btn-outline rounded-full inline-flex w-fit">
          See what&apos;s next for Pact
        </Link>
      </div>
    );
  }
  if (outcome === 'NEEDS_CLEANING') {
    return (
      <div className="space-y-2 text-sm text-neutral-700">
        <p>Give your packaging a quick clean, dry it completely, and retry verification.</p>
        <button
          type="button"
          className="btn btn-outline rounded-full"
          onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
        >
          Retry verification
        </button>
      </div>
    );
  }
  return null;
}

function PackagingSelect({ options, selectedId, onSelect, loading }) {
  return (
    <div className="form-control">
      <label className="label">
        <span className="label-text uppercase tracking-[0.14em] text-neutral">Packaging type and brand</span>
      </label>
      <div className="rounded-2xl border border-base-300">
        <div className="max-h-56 overflow-y-auto">
          {loading ? (
            <div className="p-4 text-sm text-neutral">Loading options…</div>
          ) : options.length === 0 ? (
            <div className="p-4 text-sm text-neutral">
              No matches yet. Try searching by brand name or product.
            </div>
          ) : (
            <ul className="menu menu-sm divide-y divide-base-300">
              {options.map((item) => (
                <li key={item.id}>
                  <button
                    type="button"
                    onClick={() => onSelect(item)}
                    className={`flex w-full items-start gap-3 px-4 py-3 text-left text-sm transition ${
                      selectedId === item.id
                        ? 'bg-primary/10 text-primary'
                        : 'hover:bg-base-200'
                    }`}
                  >
                    <div className="flex h-6 w-6 items-center justify-center rounded-full border border-base-300">
                      {selectedId === item.id ? (
                        <span className="h-3 w-3 rounded-full bg-primary" />
                      ) : null}
                    </div>
                    <div>
                      <p className="font-medium text-neutral-900">{item.brandName}</p>
                      <p className="text-xs text-neutral">{item.productName}</p>
                      <p className="mt-1 text-xs text-neutral/80">
                        {item.isPactAccepted ? 'Pact accepted' : 'Partially accepted'}
                        {item.materialCategory ? ` • ${item.materialCategory}` : ''}
                      </p>
                    </div>
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
    </div>
  );
}

function BinResults({ bins }) {
  if (!bins) return null;
  if (!bins.results?.length) {
    return (
      <div className="card">
        <p className="text-sm text-neutral-600">
          No Pact bins showed up near that ZIP yet. Try expanding your radius or request a mail-back label instead.
        </p>
      </div>
    );
  }

  return (
    <div className="card space-y-4">
      <h3 className="text-lg font-semibold text-neutral-900">Nearest Pact drop-off locations</h3>
      <ul className="space-y-3 text-sm text-neutral-600">
        {bins.results.map((bin) => (
          <li key={bin.storeId} className="rounded-lg border border-neutral-200 p-4">
            <p className="font-semibold text-neutral-900">{bin.storeName}</p>
            <p className="text-neutral-600">
              {bin.address}, {bin.city}, {bin.state} {bin.zipCode}
            </p>
            <p className="mt-1 text-neutral-500">
              {bin.distanceMiles.toFixed(1)} miles away • {bin.memberName}
            </p>
          </li>
        ))}
      </ul>
    </div>
  );
}

function useKnownItems() {
  const STORAGE_KEY = 'pact-known-items';
  const [items, setItems] = useState(() => {
    if (typeof window === 'undefined') return [];
    try {
      const raw = window.localStorage.getItem(STORAGE_KEY);
      return raw ? JSON.parse(raw) : [];
    } catch (error) {
      console.warn('Failed to read known items', error);
      return [];
    }
  });

  useEffect(() => {
    if (typeof window === 'undefined') return;
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(items));
  }, [items]);

  return {
    knownItems: items,
    addKnownItem: (name) => {
      if (!name) return;
      setItems((prev) => (prev.includes(name) ? prev : [...prev, name]));
    },
    removeKnownItem: (name) => {
      setItems((prev) => prev.filter((item) => item !== name));
    },
  };
}

function KnownItemsList({ items, onAdd, onRemove }) {
  const [input, setInput] = useState('');

  const handleAdd = () => {
    if (!input.trim()) return;
    onAdd(input.trim());
    setInput('');
  };

  return (
    <div className="space-y-3">
      <h3 className="text-sm font-semibold text-neutral-900">My known Pact-ready items</h3>
      <div className="flex gap-2 text-sm">
        <input
          className="input input-bordered flex-1"
          placeholder="e.g. Kiehl's Ultra Facial Cream jar"
          value={input}
          onChange={(event) => setInput(event.target.value)}
          onKeyDown={(event) => {
            if (event.key === 'Enter') {
              event.preventDefault();
              handleAdd();
            }
          }}
        />
        <button type="button" className="btn btn-outline rounded-full" onClick={handleAdd}>
          Add
        </button>
      </div>
      {items.length ? (
        <ul className="space-y-2 text-xs text-neutral-600">
          {items.map((item) => (
            <li key={item} className="flex items-center justify-between rounded-md bg-base-200 px-3 py-2">
              <span>{item}</span>
              <button
                type="button"
                className="btn btn-ghost btn-xs text-neutral hover:text-error"
                onClick={() => onRemove(item)}
              >
                Remove
              </button>
            </li>
          ))}
        </ul>
      ) : (
        <p className="text-xs text-neutral-500">Keep a quick reference list of products you already know fit Pact&apos;s guidelines.</p>
      )}
    </div>
  );
}

function Verify() {
  const { sessionId, saveSession } = useConsumerSession();
  const { user } = useConsumerAuth();
  const [email, setEmail] = useState('');
  const [zipCode, setZipCode] = useState('');
  const [isCleanConfirmed, setIsCleanConfirmed] = useState(false);
  const [packagingTerm, setPackagingTerm] = useState('');
  const [debouncedTerm, setDebouncedTerm] = useState('');
  const [packagingOptions, setPackagingOptions] = useState([]);
  const [loadingPackaging, setLoadingPackaging] = useState(false);
  const [selectedPackaging, setSelectedPackaging] = useState(null);
  const [verification, setVerification] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [binsLoading, setBinsLoading] = useState(false);
  const [binResults, setBinResults] = useState(null);
  const { knownItems, addKnownItem, removeKnownItem } = useKnownItems();

  useEffect(() => {
    const handle = setTimeout(() => setDebouncedTerm(packagingTerm), 300);
    return () => clearTimeout(handle);
  }, [packagingTerm]);

  useEffect(() => {
    if (user) {
      if (user.email) setEmail((prev) => prev || user.email);
      if (user.zip) setZipCode((prev) => prev || user.zip);
    }
  }, [user]);

  useEffect(() => {
    let active = true;
    const controller = new AbortController();
    async function loadOptions() {
      setLoadingPackaging(true);
      try {
        const data = await fetchPackaging({
          query: debouncedTerm.trim() || undefined,
          signal: controller.signal,
        });
        if (active) {
          setPackagingOptions(data);
        }
      } catch (err) {
        if (err.name !== 'AbortError') {
          console.error('Failed to load packaging catalog', err);
        }
      } finally {
        if (active) setLoadingPackaging(false);
      }
    }
    loadOptions();
    return () => {
      active = false;
      controller.abort();
    };
  }, [debouncedTerm]);

  const canSubmit = useMemo(() => {
    return Boolean(zipCode && selectedPackaging?.id && isCleanConfirmed);
  }, [zipCode, selectedPackaging, isCleanConfirmed]);

  const handleSubmit = async (event) => {
    event.preventDefault();
    setError('');

    if (!canSubmit) {
      setError('Please complete all required fields before continuing.');
      return;
    }

    setSubmitting(true);
    try {
      const payload = {
        sessionId: verification?.sessionId || sessionId || undefined,
        email: email?.trim() || undefined,
        zipCode: zipCode.trim(),
        packagingId: selectedPackaging.id,
        isCleanConfirmed: true,
      };

      const result = await submitVerification(payload);
      saveSession(result.sessionId);
      setVerification(result);
      setBinResults(null);
      await recordEvent({
        eventType: 'verification_submitted',
        sessionId: result.sessionId,
        payload: { outcome: result.outcome, packagingId: selectedPackaging.id },
      });
    } catch (err) {
      console.error(err);
      setError(err.message);
    } finally {
      setSubmitting(false);
    }
  };

  const handleFindBins = async () => {
    if (!zipCode) return;
    setBinsLoading(true);
    setBinResults(null);
    try {
      const results = await searchBins({ zip: zipCode });
      setBinResults(results);
      await recordEvent({
        eventType: 'bin_search_from_verify',
        sessionId: verification?.sessionId || sessionId || null,
        payload: { zip: zipCode, count: results?.count || 0 },
      });
    } catch (err) {
      console.error(err);
      setError(err.message);
    } finally {
      setBinsLoading(false);
    }
  };

  return (
    <div className="space-y-8">
      <div className="space-y-3">
        <h1 className="text-3xl font-semibold text-neutral-900">Verify your packaging</h1>
        <p className="max-w-2xl text-sm text-neutral-600">
          Pact accepts clean, empty packaging. Confirm your item details and we’ll guide you to the best end-of-life option. Need a bin right away? <a href="/bin-finder" className="text-emerald-700 underline">Use the bin finder</a> to search by ZIP code.
        </p>
      </div>

      <form onSubmit={handleSubmit} className="card bg-base-100 shadow-lg">
        <div className="card-body space-y-6">
          <div className="grid gap-6 md:grid-cols-2">
            <div className="space-y-4">
              <div className="form-control">
                <label className="label" htmlFor="email">
                  <span className="label-text uppercase tracking-[0.14em] text-neutral">Email (optional)</span>
                </label>
                <input
                  id="email"
                  type="email"
                  className="input input-bordered"
                  value={email}
                  onChange={(event) => setEmail(event.target.value)}
                  placeholder="you@example.com"
                />
                <label className="label">
                  <span className="label-text-alt text-xs text-neutral">
                    Add your email to receive a verification recap and mail-back updates.
                  </span>
                </label>
              </div>

              <div className="form-control">
                <label className="label" htmlFor="zip">
                  <span className="label-text uppercase tracking-[0.14em] text-neutral">ZIP code</span>
                </label>
                <input
                  id="zip"
                  inputMode="numeric"
                  className="input input-bordered"
                  value={zipCode}
                  onChange={(event) => setZipCode(event.target.value)}
                  placeholder="e.g. 11222"
                  required
                />
              </div>

              <label className="flex items-start gap-3 rounded-xl border border-base-300 bg-base-200 p-4 text-sm text-neutral">
                <input
                  type="checkbox"
                  className="checkbox checkbox-primary mt-1"
                  checked={isCleanConfirmed}
                  onChange={(event) => setIsCleanConfirmed(event.target.checked)}
                />
                <span>
                  I confirm my packaging is clean, empty, and dry. Pact cannot process packaging with leftover product, pumps filled with product, or residual liquids.
                </span>
              </label>
              <KnownItemsList items={knownItems} onAdd={addKnownItem} onRemove={removeKnownItem} />
            </div>

            <div className="space-y-4">
              <div className="form-control">
                <label className="label" htmlFor="packaging-search">
                  <span className="label-text uppercase tracking-[0.14em] text-neutral">Search by brand or product</span>
                </label>
                <input
                  id="packaging-search"
                  className="input input-bordered"
                  value={packagingTerm}
                  onChange={(event) => setPackagingTerm(event.target.value)}
                  placeholder="e.g. Kiehl's serum bottle"
                />
              </div>
              <PackagingSelect
                options={packagingOptions}
                selectedId={selectedPackaging?.id}
                onSelect={setSelectedPackaging}
                loading={loadingPackaging}
              />
            </div>
          </div>

          {error ? <div className="alert alert-error text-sm">{error}</div> : null}

          <div className="flex flex-wrap items-center gap-3">
            <button type="submit" className="btn btn-primary rounded-full" disabled={submitting || !canSubmit}>
              {submitting ? 'Checking...' : 'Check packaging'}
            </button>
            {verification?.outcome ? (
              <button
                type="button"
                className="btn btn-outline rounded-full"
                onClick={handleFindBins}
                disabled={binsLoading}
              >
                {binsLoading ? 'Searching bins…' : 'Find a Pact bin nearby'}
              </button>
            ) : null}
            {verification?.outcome === 'PACT' || verification?.outcome === 'PACT_AND_OTHER' ? (
              <Link to="/mail-back" className="btn btn-outline rounded-full">
                Request a mail-back label
              </Link>
            ) : null}
          </div>
        </div>
      </form>

      <ResultPanel verification={verification} />

      <BinResults bins={binResults} />
    </div>
  );
}

export default Verify;
